import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@vwfs-bronson/bronson-react';
import { isNotesEnabled } from '../../../../services/common/miscUtils';
import { setFilters, setIntent } from '../../../../services/redux/features/notes.slice';
import { notificationCenterSelectors } from '../../../../services/redux/features/notificationCenter.slice';
import { NotesState } from '../../../../services/redux/models/NotesState';
import { useNavigate } from '../../../../services/routing/navigate';
import { getReferenceData } from '../../../NotesPage/conversationUtils';
import { notesFiltersInitialValues } from '../../../NotesPage/Filters';

export type ConversationStatusInput = {
  conversationId?: string;
  unreadMessages?: number;
  isSeenByCurrentUser?: boolean;
};

export const useDashboardNotesUtils = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const notificationsStoreConversations = useSelector(notificationCenterSelectors.selectConversations);

  const getNotesStatus = (
    hasConversations: boolean,
    areAllConversationsRead: boolean
  ): 'create' | 'unread' | 'read' => {
    if (!hasConversations) return 'create';
    if (!areAllConversationsRead) return 'unread';
    return 'read';
  };

  const computeConversationStatus = (conversations: ConversationStatusInput[]) => {
    const unread = conversations.filter((item) => (item.unreadMessages ?? 0) > 0);
    const hasConversations = conversations.length > 0;
    const areAllConversationsRead =
      unread.length === 0 && hasConversations && conversations.every((item) => item.isSeenByCurrentUser);
    return {
      hasConversations,
      areAllConversationsRead,
      firstUnreadConversationId: unread[0]?.conversationId ?? null,
    };
  };

  const getNotesStatusElement = (
    hasConversations: boolean,
    humanReadableObjectReferenceId: string,
    objectType: MilesPosNotesIntegrationService.Components.Schemas.ContextRepresentationsEnum,
    firstUnreadConversationId: string | null,
    areAllConversationsRead: boolean
  ) => {
    const handleClick = (intent: NotesState['intent']['type']) => {
      dispatch(
        setFilters({
          ...notesFiltersInitialValues,
          objectType,
          humanReadableObjectReferenceId,
        })
      );
      dispatch(setIntent({ type: intent, conversationId: firstUnreadConversationId }));
      navigate.dashboardConversations();
    };

    const status = getNotesStatus(hasConversations, areAllConversationsRead);
    switch (status) {
      case 'create':
        return (
          <Button link onClick={() => handleClick('CREATE_CONVERSATION')}>
            {t('dashboardNotesUtils:create')}
          </Button>
        );
      case 'unread':
        return (
          <Button link onClick={() => handleClick('READ')} iconReversed icon="semantic-notification">
            {t('dashboardNotesUtils:unread')}
          </Button>
        );
      case 'read':
      default:
        return (
          <Button link onClick={() => handleClick('READ')}>
            {t('dashboardNotesUtils:read')}
          </Button>
        );
    }
  };

  const getNotesDashboardButton = (
    objectType: MilesPosNotesIntegrationService.Components.Schemas.FindConversationContextRepresentation,
    humanReadableObjectReferenceId: string | undefined, // That is human readable reference id
    systemObjectId: string // That is the id of the object in the Miles system, that conversation is linked to
  ) => {
    if (!isNotesEnabled()) {
      return null;
    }

    if (!objectType || !humanReadableObjectReferenceId || !systemObjectId) {
      return null;
    }

    const conversations = (notificationsStoreConversations ?? []).filter((item) => {
      const referenceData = getReferenceData(item);

      switch (referenceData?.objectType) {
        case 'SALES_QUOTE':
          return item.referenceMap?.salesQuoteId === systemObjectId;
        case 'CREDIT_APPLICATION':
          return item.referenceMap?.creditApplicationId === systemObjectId;
        case 'PENDING_CONTRACT':
          return item.referenceMap?.pendingContractId === systemObjectId;
        case 'CONTRACT':
          return item.referenceMap?.contractId === systemObjectId;
        default:
          return false;
      }
    });

    const { hasConversations, areAllConversationsRead, firstUnreadConversationId } =
      computeConversationStatus(conversations);

    return getNotesStatusElement(
      hasConversations,
      humanReadableObjectReferenceId,
      objectType,
      firstUnreadConversationId,
      areAllConversationsRead
    );
  };

  const getNotesDashboardButtonFromConversations = (
    objectType: MilesPosNotesIntegrationService.Components.Schemas.ContextRepresentationsEnum,
    humanReadableObjectReferenceId: string | undefined,
    conversations: ConversationStatusInput[] | undefined
  ) => {
    if (!isNotesEnabled()) {
      return null;
    }

    if (!objectType || !humanReadableObjectReferenceId) {
      return null;
    }

    const { hasConversations, areAllConversationsRead, firstUnreadConversationId } = computeConversationStatus(
      conversations ?? []
    );

    return getNotesStatusElement(
      hasConversations,
      humanReadableObjectReferenceId,
      objectType,
      firstUnreadConversationId,
      areAllConversationsRead
    );
  };

  return {
    getNotesDashboardButton,
    getNotesDashboardButtonFromConversations,
  };
};
